import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import ButtonOne from "../../common/normal-button";
import ButtonTwo from "../../common/dashboard-button";
import PhoneInput from "../../common/phone-input";

const FIELDS = ["name", "loan_account_number", "country_code", "phone_number", "due_day", "payment_amount"];
const EMPTY_ERRORS = Object.fromEntries(FIELDS.map((f) => [f, ""]));

export const ACTIVE_SWITCH_SX = {
  width: 36,
  height: 20,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: "2px",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": { backgroundColor: "#1E7A4C", opacity: 1 },
    },
  },
  "& .MuiSwitch-thumb": { width: 16, height: 16, boxShadow: "none" },
  "& .MuiSwitch-track": { borderRadius: 10, backgroundColor: "#C8D0E0", opacity: 1 },
};

// Same rules as the backend (emi_reminder/validators.py) so most mistakes are
// caught before saving; the server's field errors are still shown if any.
const validateForm = (form) => {
  const errors = { ...EMPTY_ERRORS };
  const name = form.name.trim();
  if (!name) errors.name = "Name is required";
  else if (name.length > 255) errors.name = "Name must be at most 255 characters";

  const loan = form.loan_account_number.trim();
  if (loan.length > 50) errors.loan_account_number = "Loan A/C Number must be at most 50 characters";
  else if (loan && !/^[A-Za-z0-9/\- ]+$/.test(loan))
    errors.loan_account_number = 'Only letters, digits, "-", "/" and spaces are allowed';

  const cc = form.country_code.replace("+", "");
  const phone = form.phone_number.trim();
  if (!phone) errors.phone_number = "Phone Number is required";
  else if (!/^\d+$/.test(phone)) errors.phone_number = "Phone number must contain digits only";
  else if (cc === "91" && phone.length !== 10) errors.phone_number = "Enter a valid 10-digit mobile number";
  else if (cc !== "91" && (phone.length < 6 || phone.length > 15 - cc.length))
    errors.phone_number = "Please enter a valid phone number";

  const day = String(form.due_day).trim();
  if (!day) errors.due_day = "Due Date is required";
  else if (!/^\d+$/.test(day)) errors.due_day = "Digits only (day of the month)";
  else if (Number(day) < 1 || Number(day) > 31) errors.due_day = "Must be between 1 and 31";

  const amount = String(form.payment_amount).trim();
  if (amount) {
    if (!/^\d+(\.\d{1,2})?$/.test(amount)) errors.payment_amount = "Enter a positive amount (max 2 decimals)";
    else if (Number(amount) >= 1e10) errors.payment_amount = "Amount is too large";
  }
  return errors;
};

const EMIRecordFormDialog = ({ open, record, onClose, onSave, saving = false }) => {
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState(EMPTY_ERRORS);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!open || !record) return;
    setForm({
      name: record.name || "",
      loan_account_number: record.loan_account_number || "",
      country_code: `+${record.country_code || "91"}`,
      phone_number: record.phone_number || "",
      due_day: record.due_day ?? "",
      payment_amount: record.payment_amount ?? "",
      active: record.active ?? true,
    });
    setErrors(EMPTY_ERRORS);
    setFormError("");
  }, [open, record]);

  if (!form) return null;

  const set = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field in EMPTY_ERRORS) setErrors((prev) => ({ ...prev, [field]: "" }));
    setFormError("");
  };

  const handleSave = async () => {
    const nextErrors = validateForm(form);
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    const result = await onSave({
      name: form.name.trim(),
      loan_account_number: form.loan_account_number.trim(),
      country_code: form.country_code.replace("+", ""),
      phone_number: form.phone_number.trim(),
      due_day: String(form.due_day).trim(),
      payment_amount: String(form.payment_amount).trim() || null,
      active: form.active,
    });

    // Show the server's validation errors against their fields.
    if (result?.errors) {
      const fieldErrors = { ...EMPTY_ERRORS };
      const other = [];
      Object.entries(result.errors).forEach(([field, msgs]) => {
        const msg = Array.isArray(msgs) ? msgs.join(" ") : String(msgs);
        if (field in fieldErrors) fieldErrors[field] = msg;
        else other.push(msg);
      });
      setErrors(fieldErrors);
      setFormError(other.join(" "));
    } else if (result?.error) {
      setFormError(result.error);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        Edit Customer
        <IconButton onClick={onClose} disabled={saving}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          {formError && <Alert severity="error">{formError}</Alert>}

          <TextField
            label="Customer Name"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            fullWidth
            required
            error={!!errors.name}
            helperText={errors.name}
          />

          <TextField
            label="Loan A/C Number"
            value={form.loan_account_number}
            onChange={(e) => set("loan_account_number", e.target.value)}
            fullWidth
            error={!!errors.loan_account_number}
            helperText={errors.loan_account_number}
          />

          <PhoneInput
            countryCode={form.country_code}
            phoneNo={form.phone_number}
            onCountryCodeChange={(v) => set("country_code", v)}
            onPhoneNoChange={(v) => set("phone_number", v)}
            required
            error={!!(errors.phone_number || errors.country_code)}
            helperText={errors.phone_number || errors.country_code}
            fullWidth
          />

          <Box display="flex" gap={2}>
            <TextField
              label="Due Date (day of month)"
              value={form.due_day}
              onChange={(e) => set("due_day", e.target.value)}
              fullWidth
              required
              inputProps={{ inputMode: "numeric" }}
              error={!!errors.due_day}
              helperText={errors.due_day || "1 to 31"}
            />
            <TextField
              label="EMI Amount"
              value={form.payment_amount}
              onChange={(e) => set("payment_amount", e.target.value)}
              fullWidth
              inputProps={{ inputMode: "decimal" }}
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              error={!!errors.payment_amount}
              helperText={errors.payment_amount || "Optional"}
            />
          </Box>

          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography variant="body2">Active</Typography>
            <Switch
              checked={form.active}
              onChange={(e) => set("active", e.target.checked)}
              size="small"
              sx={ACTIVE_SWITCH_SX}
            />
            <Typography variant="caption" color="text.secondary">
              {form.active
                ? "Receives automated EMI reminders"
                : "No automated EMI reminders while inactive"}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, px: 3 }}>
        <ButtonTwo onClick={onClose} disabled={saving}>
          Cancel
        </ButtonTwo>
        <ButtonOne onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </ButtonOne>
      </DialogActions>
    </Dialog>
  );
};

export default EMIRecordFormDialog;
